import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ShieldCheck, AlertTriangle, Loader2, Eye, EyeOff, Smartphone, Copy, Check } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";

export default function AdminLogin() {
  const { user, loading, login, verifyTotp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(null); // { step, challenge_token, qr_png_base64?, secret?, otpauth_uri? }
  const [code, setCode] = useState("");
  const codeRef = useRef(null);

  useEffect(() => {
    if (challenge && codeRef.current) codeRef.current.focus();
  }, [challenge]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }
  if (user) return <Navigate to="/admin" replace />;

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const data = await login(email, password);
      setChallenge(data);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await verifyTotp(challenge.challenge_token, code);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => { setChallenge(null); setCode(""); setError(""); };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 te-noise" style={{ backgroundColor: "var(--te-bg)" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 flex items-center justify-center" style={{ background: "var(--te-primary)" }}>
            <ShieldCheck size={20} color="#fff" />
          </div>
          <div className="font-bold tracking-tight">TECH EMERGENCY · ADMIN</div>
        </div>

        {!challenge && (
          <form onSubmit={submitPassword} className="te-card p-8 space-y-5" data-testid="admin-login-form">
            <div>
              <h1 className="text-2xl font-bold tracking-tight mb-1">Sign in</h1>
              <p className="text-sm text-zinc-400">Admin access only.</p>
            </div>
            <label className="block">
              <div className="te-overline text-[10px] mb-2">Email</div>
              <input
                data-testid="admin-login-email"
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="te-input" autoFocus
              />
            </label>
            <label className="block">
              <div className="te-overline text-[10px] mb-2">Password</div>
              <div className="relative">
                <input
                  data-testid="admin-login-password"
                  type={showPw ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="te-input pr-10"
                />
                <button type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="admin-login-show-password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white p-1"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>
            <ErrorBox error={error} testid="admin-login-error" />
            <button type="submit" disabled={submitting} data-testid="admin-login-submit"
              className="te-btn te-btn-primary w-full disabled:opacity-50">
              {submitting ? "Signing in…" : "Continue"}
            </button>
          </form>
        )}

        {challenge && (
          <TotpStep
            challenge={challenge}
            issuer="Tech Emergency · Admin"
            email={email}
            code={code}
            setCode={setCode}
            codeRef={codeRef}
            submit={submitCode}
            submitting={submitting}
            restart={restart}
            error={error}
            testidPrefix="admin-2fa"
          />
        )}
      </div>

      <style>{`
        .te-input {
          width: 100%; background: var(--te-surface);
          border: 1px solid var(--te-border); color: var(--te-text);
          padding: 0.75rem 0.875rem; font-family: 'IBM Plex Sans', sans-serif;
          font-size: 0.95rem; outline: none;
        }
        .te-input:focus { border-color: var(--te-primary); }
        .te-code-input {
          width: 100%; background: var(--te-surface);
          border: 1px solid var(--te-border); color: var(--te-text);
          padding: 0.85rem 1rem; font-family: 'JetBrains Mono', monospace;
          font-size: 1.6rem; letter-spacing: 0.4em; text-align: center;
          outline: none;
        }
        .te-code-input:focus { border-color: var(--te-primary); }
      `}</style>
    </div>
  );
}

export function TotpStep({ challenge, issuer, email, code, setCode, codeRef, submit, submitting, restart, error, testidPrefix }) {
  const isSetup = challenge.step === "setup";
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(challenge.secret); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <form onSubmit={submit} className="te-card p-8 space-y-5" data-testid={`${testidPrefix}-form`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{ background: "var(--te-primary)" }}>
          <Smartphone size={18} color="#fff" />
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight">
            {isSetup ? "Set up two-factor auth" : "Enter your 6-digit code"}
          </h1>
          <p className="text-xs text-zinc-400 mt-1">
            {isSetup
              ? "We require an authenticator app on first login. It's a one-time setup."
              : `From Google Authenticator, Authy, 1Password, or whichever app you used to set up ${issuer}.`}
          </p>
        </div>
      </div>

      {isSetup && (
        <div className="space-y-3">
          <ol className="text-sm text-zinc-300 space-y-2 list-decimal pl-5">
            <li>Install <strong>Google Authenticator</strong> (or Authy, 1Password, etc.) on your phone.</li>
            <li>Open it and tap <strong>+ → Scan a QR code</strong>.</li>
            <li>Scan the code below with your authenticator app.</li>
            <li>Type the 6-digit code it shows.</li>
          </ol>
          <div className="flex flex-col items-center gap-2 p-4" style={{ background: "#fff" }} data-testid={`${testidPrefix}-qr`}>
            <img
              alt="Scan with your authenticator app"
              src={`data:image/png;base64,${challenge.qr_png_base64}`}
              style={{ width: 200, height: 200, display: "block" }}
            />
          </div>
          <details className="text-xs text-zinc-400">
            <summary className="cursor-pointer hover:text-white">Can't scan? Enter this code manually</summary>
            <div className="mt-2 flex items-center gap-2 p-3 te-card font-mono text-[13px] break-all">
              <code className="flex-1" data-testid={`${testidPrefix}-secret`}>{challenge.secret}</code>
              <button type="button" onClick={copy} className="text-zinc-400 hover:text-white" aria-label="Copy">
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">Account: <code>{email}</code> · Issuer: {issuer}</div>
          </details>
        </div>
      )}

      <label className="block">
        <div className="te-overline text-[10px] mb-2">6-digit code</div>
        <input
          ref={codeRef}
          data-testid={`${testidPrefix}-code`}
          type="text" inputMode="numeric" autoComplete="one-time-code"
          required maxLength={6} pattern="\d{6}"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="te-code-input"
          placeholder="000000"
        />
      </label>

      <ErrorBox error={error} testid={`${testidPrefix}-error`} />

      <div className="flex gap-2">
        <button type="button" onClick={restart}
          className="te-btn te-btn-ghost text-sm flex-shrink-0"
          data-testid={`${testidPrefix}-back`}
        >
          ← Back
        </button>
        <button type="submit" disabled={submitting || code.length !== 6}
          data-testid={`${testidPrefix}-submit`}
          className="te-btn te-btn-primary flex-1 disabled:opacity-50"
        >
          {submitting ? "Verifying…" : (isSetup ? "Activate & sign in" : "Verify")}
        </button>
      </div>
    </form>
  );
}

function ErrorBox({ error, testid }) {
  if (!error) return null;
  return (
    <div className="flex items-start gap-2 p-3 border" style={{ borderColor: "var(--te-primary)", background: "rgba(255,59,48,0.08)" }} data-testid={testid}>
      <AlertTriangle size={16} style={{ color: "var(--te-primary)" }} className="mt-0.5" />
      <span className="text-sm text-zinc-200">{error}</span>
    </div>
  );
}
